/**
 * WCL-shaped export read by tools/analyze_run.py: numeric actor/ability ids, WCL numeric
 * hitType, melee = abilityGameID 1, players typed 'Player' with their class as subType.
 */
import type { AbilityId, ContentRegistry, HitType, School, SimEvent, SimEventType, UnitId, WclExport, World } from '../contracts';
import { MELEE_ABILITY_ID } from '../contracts';
import type { SegmentRecord, UnitMeta } from './meta';
import { membersOfPacks, metaOf } from './meta';

/** WCL hitType numbers. */
export const WCL_HIT_TYPE: Readonly<Record<HitType, number>> = {
  miss: 0,
  hit: 1,
  crit: 2,
  absorb: 3,
  block: 4,
  dodge: 7,
  parry: 8,
  immune: 10,
};

/** WCL school bitmask (ability `type`). */
export const WCL_SCHOOL: Readonly<Record<School, number>> = {
  physical: 1,
  holy: 2,
  fire: 4,
  nature: 8,
  frost: 16,
  shadow: 32,
  arcane: 64,
};

/** Events that exist in WCL; sim-only events (pulls, telegraphs...) are not exported. */
const WCL_EVENT_TYPES: ReadonlySet<SimEventType> = new Set<SimEventType>([
  'damage',
  'heal',
  'absorbed',
  'begincast',
  'cast',
  'applybuff',
  'applydebuff',
  'removebuff',
  'removedebuff',
  'refreshbuff',
  'refreshdebuff',
  'applybuffstack',
  'applydebuffstack',
  'removebuffstack',
  'removedebuffstack',
  'interrupt',
  'dispel',
  'death',
  'resourcechange',
  'summon',
]);

const UNIT_FIELDS = ['sourceID', 'targetID', 'attackerID'] as const;
const ABILITY_FIELDS = ['abilityGameID', 'extraAbilityGameID'] as const;

/** Base gameID for NPC definitions (index in the content bundle is added). */
export const NPC_GAME_ID_BASE = 100_000;
export const MELEE_GAME_ID = 1;

export interface WclExportInput {
  world: World;
  content: ContentRegistry;
  events: readonly SimEvent[];
  meta: ReadonlyMap<UnitId, UnitMeta>;
  segments: readonly SegmentRecord[];
}

export function buildWclExport({ world, content, events, meta: logMeta, segments }: WclExportInput): WclExport {
  // Unit metadata: live world first, then units only the log still remembers (despawned).
  const meta = new Map<UnitId, UnitMeta>();
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (u) meta.set(id, metaOf(u));
  }
  for (const [id, m] of logMeta) if (!meta.has(id)) meta.set(id, m);

  // Actor ids: party in slot order, then everyone else in spawn order.
  const actorId = new Map<UnitId, number>();
  const order = [...world.partyIds, ...meta.keys()];
  for (const id of order) if (!actorId.has(id) && meta.has(id)) actorId.set(id, actorId.size + 1);

  const npcIndex = new Map(content.bundle.npcs.map((n, i) => [n.id, i] as const));
  const extraNpc = new Map<string, number>();
  const npcGameId = (npcId: string): number => {
    const i = npcIndex.get(npcId);
    if (i !== undefined) return NPC_GAME_ID_BASE + i + 1;
    if (!extraNpc.has(npcId)) extraNpc.set(npcId, NPC_GAME_ID_BASE + npcIndex.size + extraNpc.size + 1);
    return extraNpc.get(npcId)!;
  };

  const actors: WclExport['masterData']['actors'] = [];
  for (const [id, num] of actorId) {
    const m = meta.get(id)!;
    const owner = m.summonerId ? meta.get(m.summonerId) : undefined;
    const isPet = m.kind === 'npc' && m.faction === 'party' && owner?.kind === 'player';
    if (m.kind === 'player') {
      const cls = m.classId ? content.class(m.classId) : undefined;
      actors.push({ id: num, gameID: 0, name: m.name, type: 'Player', subType: cls?.name ?? m.classId ?? 'Unknown', petOwner: null });
    } else if (isPet) {
      actors.push({ id: num, gameID: m.npcId ? npcGameId(m.npcId) : 0, name: m.name, type: 'Pet', subType: 'Pet', petOwner: actorId.get(owner!.id) ?? null });
    } else {
      actors.push({ id: num, gameID: m.npcId ? npcGameId(m.npcId) : 0, name: m.name, type: 'NPC', subType: m.tier === 'boss' ? 'Boss' : 'NPC', petOwner: null });
    }
  }

  // Ability ids: melee = 1, everything else numbered by first appearance.
  const abilityGameId = new Map<AbilityId, number>([[MELEE_ABILITY_ID, MELEE_GAME_ID]]);
  const abilityNum = (id: AbilityId): number => {
    let n = abilityGameId.get(id);
    if (n === undefined) {
      n = abilityGameId.size + 1;
      abilityGameId.set(id, n);
    }
    return n;
  };

  const friendly: Record<string, unknown>[] = [];
  for (const e of events) {
    if (!WCL_EVENT_TYPES.has(e.type)) continue;
    const out: Record<string, unknown> = { fight: 1 };
    for (const [k, v] of Object.entries(e)) {
      if (v === undefined) continue;
      if ((UNIT_FIELDS as readonly string[]).includes(k)) out[k] = actorId.get(v as UnitId) ?? -1;
      else if ((ABILITY_FIELDS as readonly string[]).includes(k)) out[k] = abilityNum(v as AbilityId);
      else if (k === 'hitType') out[k] = WCL_HIT_TYPE[v as HitType] ?? 1;
      else out[k] = v;
    }
    friendly.push(out);
  }

  const abilities: WclExport['masterData']['abilities'] = [];
  for (const [id, gameID] of abilityGameId) {
    if (id === MELEE_ABILITY_ID) {
      abilities.push({ gameID, name: 'Melee', type: String(WCL_SCHOOL.physical), icon: 'ability_meleedamage' });
      continue;
    }
    const ab = content.ability(id);
    const aura = ab ? undefined : content.aura(id);
    abilities.push({
      gameID,
      name: ab?.name ?? aura?.name ?? id,
      type: String(WCL_SCHOOL[ab?.school ?? 'physical']),
      icon: ab?.ui?.icon ?? 'inv_misc_questionmark',
    });
  }

  const m = world.mplus;
  const startTime = m.startedAt ?? events[0]?.timestamp ?? 0;
  const endTime = m.completedAt ?? world.time;

  const dungeonPulls: WclExport['fight']['dungeonPulls'] = segments.map((seg) => {
    const members = seg.memberIds.length > 0 ? seg.memberIds : membersOfPacks(world, seg.packIds);
    const byNpc = new Map<number, { gameID: number; id: number; count: number }>();
    for (const id of members) {
      const mm = meta.get(id);
      if (!mm?.npcId) continue;
      const gid = npcGameId(mm.npcId);
      const entry = byNpc.get(gid);
      if (entry) entry.count += 1;
      else byNpc.set(gid, { gameID: gid, id: actorId.get(id) ?? -1, count: 1 });
    }
    const firstName = members.map((id) => meta.get(id)?.name).find(Boolean);
    const name = (seg.encounterId && content.encounter(seg.encounterId)?.name) || firstName || `Pull ${seg.pull}`;
    return {
      id: seg.pull,
      name,
      startTime: seg.startMs,
      endTime: seg.endMs ?? endTime,
      kill: seg.endMs !== null && !seg.wipe,
      enemyNPCs: [...byNpc.values()].map((n) => ({ gameID: n.gameID, id: n.id, minimumInstanceID: 1, maximumInstanceID: n.count })),
    };
  });

  return {
    code: `sim-${world.seed}`,
    fight: {
      id: 1,
      name: content.dungeon(world.dungeonId)?.name ?? world.dungeonId,
      startTime,
      endTime,
      keystoneLevel: m.level,
      keystoneTime: m.elapsedMs,
      keystoneBonus: m.chest ?? 0,
      kill: m.phase === 'completed' || m.phase === 'depleted',
      dungeonPulls,
    },
    masterData: { actors, abilities },
    friendly,
  };
}
